/**
 * Liveness checks for the stdio streams the logging layer writes to.
 *
 * Shared by `StdoutSink` (which stops writing to a dead stream) and the
 * runner's `watchOutputStream` (which stops the loop, and exits only when its
 * caller owns the process), so the two cannot disagree about what "dead" means.
 * See `_devlog/notes/2026-09-07-stdout-epipe-log-loop.md`.
 */

/**
 * Structural subset of `process.stdout` these helpers need. Declared instead of
 * `NodeJS.WriteStream` so tests and embedders can pass an ordinary `Writable`.
 */
export type OutputStream = NodeJS.WritableStream & {
  isTTY?: boolean;
  destroyed?: boolean;
  writableEnded?: boolean;
};

function codeOf(err: unknown): string | undefined {
  if (typeof err !== "object" || err === null) return undefined;
  const code = (err as NodeJS.ErrnoException).code;
  return typeof code === "string" ? code : undefined;
}

/**
 * True only for `EPIPE` — the reader closed its end.
 *
 * Deliberately narrow, because callers act on it by killing the process: a
 * wider set would let an unrelated bad descriptor take a whole session down.
 * Use {@link isUnwritableStreamError} to decide whether to stop *writing*.
 */
export function isBrokenPipeError(err: unknown): boolean {
  return codeOf(err) === "EPIPE";
}

/**
 * Error codes meaning this stream will never accept another write. Broader
 * than {@link isBrokenPipeError} because latching a sink off is cheap and
 * reversible in a way that exiting the process is not.
 */
const UNWRITABLE_CODES = new Set([
  "EPIPE",
  "EBADF",
  "ERR_STREAM_DESTROYED",
  "ERR_STREAM_WRITE_AFTER_END",
]);

/** True when a further write to this stream can only fail the same way. */
export function isUnwritableStreamError(err: unknown): boolean {
  const code = codeOf(err);
  return code !== undefined && UNWRITABLE_CODES.has(code);
}

/** False once the stream has been destroyed or ended. */
export function isStreamOpen(stream: { destroyed?: boolean; writableEnded?: boolean }): boolean {
  return stream.destroyed !== true && stream.writableEnded !== true;
}

/**
 * Streams known to be unwritable. Sinks consult this and go quiet; nothing
 * here listens for `error`, because claiming that event suppresses Node's
 * default fatal-EPIPE exit — right for a long-lived server, wrong for a CLI
 * being piped into `head`. That listener belongs to whoever owns the process
 * lifecycle: see `watchOutputStream` in the runner.
 */
let brokenStreams = new WeakSet<object>();

/** Record that a stream will never accept another write. Idempotent. */
export function markStreamBroken(stream: object): void {
  brokenStreams.add(stream);
}

/**
 * Forget every broken-stream mark. Test-only — the registry is process-wide by
 * design, so a suite that breaks a stream would otherwise leak that verdict.
 * @internal
 */
export function __resetBrokenStreams(): void {
  brokenStreams = new WeakSet();
}

/** True while a stream is worth writing to. */
export function isStreamUsable(stream: { destroyed?: boolean; writableEnded?: boolean }): boolean {
  return !brokenStreams.has(stream) && isStreamOpen(stream);
}
